import type { Security } from '@src/types/Security';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get, isArray } from 'lodash-es';
import type { AgGridSecurityProps } from '../../AgGrid';
import { SecuritiesRenderer } from '../../SecurityRenderer';
import { wrapDisplayedSecurityDescriptionQuickFilter } from './assetQuickFilterText';
import { baseColumn } from './baseColumn';
import { resolveSecurityColumnShowDescription } from './securityDescription';
import { wrapSecurityIdentifierQuickFilterText } from './securityIdentifiers';
import type { ColDefFactory, Column } from './types';

export type SecurityColumnParams = AgGridSecurityProps & {
  includeSecurityIdentifiersInQuickFilter?: boolean;
};

type SecurityColumnValue = string | string[] | undefined;

function securityCellRendererParams(
  params: SecurityColumnParams | undefined,
  showDescription: AgGridSecurityProps['showDescription']
): AgGridSecurityProps {
  if (params == null) {
    return { showDescription };
  }

  const { includeSecurityIdentifiersInQuickFilter: _includeSecurityIdentifiersInQuickFilter, ...rendererParams } =
    params;
  return { ...rendererParams, showDescription };
}

/**
 * Column to render Security (option, future, etc.) values from Field or securityCurrencyField param (if available)
 *
 * See {@link AgGridSecurityProps} for parameter options.
 */
export const security: ColDefFactory<Column<SecurityColumnParams>> = column => {
  const showDescription = resolveSecurityColumnShowDescription(column.params);
  let getQuickFilterText = wrapDisplayedSecurityDescriptionQuickFilter(column.getQuickFilterText, { showDescription });
  if (column.params?.includeSecurityIdentifiersInQuickFilter === true) {
    getQuickFilterText = wrapSecurityIdentifierQuickFilterText(getQuickFilterText);
  }

  return {
    initialWidth: 100,
    ...baseColumn(column),
    getQuickFilterText,
    valueGetter: (params: ValueGetterParams<unknown>): SecurityColumnValue => {
      if (!column.field || !params.node?.data) {
        return undefined;
      }

      const value = get(params.node.data, column.field);
      const isValueEmpty =
        value == null || (typeof value === 'string' && value === '') || (Array.isArray(value) && value.length === 0);
      if (isValueEmpty && column.params?.showAsteriskOnEmpty) {
        return '*';
      }

      return value;
    },
    tooltipValueGetter: column.tooltipValueGetter ?? (params => params.data?.description),
    cellRendererSelector: (params: ICellRendererParams<unknown, SecurityColumnValue>) => {
      const value = params.value;
      if (isArray(value)) {
        return {
          component: () => <SecuritiesRenderer symbols={value} showIcon={column.params?.showIcon} />,
        };
      }

      return {
        params,
        component: 'securityColumn',
      };
    },
    cellRendererParams: securityCellRendererParams(column.params, showDescription),
    valueFormatter: ({ context, value }: ValueFormatterParams<unknown, SecurityColumnValue>) => {
      if (!value) {
        return '';
      }

      // The ValueFormatter will be used for searching and for exports. The CellRenderer is what renders the symbols into the view itself
      // So here we are happy to render _all_ the symbols. In the cell renderer however, we will be doing something more short-form.
      if (Array.isArray(value)) {
        const symbols = value
          .map(symbol => context.current.securitiesBySymbol?.get(symbol)?.DisplaySymbol ?? symbol)
          .join(', ');

        // Attach "Multi" for consistency with the cell renderer, but also to get "Multi" to match when searching in the blotter
        return `Multi (${value.length}): ${symbols}`;
      }

      // value is a string down here
      const security: Security | undefined = context.current.securitiesBySymbol?.get(value);
      return security?.DisplaySymbol ?? value;
    },
  };
};
